import React from "react";
import { Modal, Button, Form, Row, Col, Accordion, Spinner } from "react-bootstrap";
import FormAssignmentProjectSelector from "./FormAssignmentProjectSelector";
import FormAssignmentModuleSelector from "./FormAssignmentModuleSelector";
import FormAssignmentFormSelector from "./FormAssignmentFormSelector";
import FormAssignmentUserSelector from "./FormAssignmentUserSelector";
import EmployeePicker from "../shared/EmployeePicker";

// Shared Assign Form wizard modal -- business fields (Project, Module,
// Form Type, Published Form, Assign To, Due Date, Instructions,
// submission settings) are identical for Admin and every other role, so
// this ONE component is rendered by both AdminFormAssignmentsPage and
// RoleFormAssignmentsPage via useFormAssignments(). Only the PAGE around
// it differs; this modal's own markup/business logic is not duplicated.
//
// Visual redesign only (no field, handler, or validation change): grouped
// into labeled "afm-section" steps with icons so a dense 12-field form
// reads as a short, scannable sequence instead of one flat block, plus
// pill-style toggles for the submission-rule checkboxes. Every value/
// onChange/disabled prop below is byte-for-byte the same as before.
export default function AssignFormModal({
  show, onClose, form, users, selectedFormRow, saving,
  onChange, onFormChange, onSave, modalClassName = "unit-modal form-assign-modal",
}) {
  return (
    <Modal show={show} onHide={onClose} centered size="lg" className={modalClassName}>
      <Modal.Header closeButton className="form-assign-modal-header">
        <div className="afm-header-flex">
          <div className="afm-header-icon"><i className="bi bi-send-plus" aria-hidden="true" /></div>
          <div>
            <Modal.Title>Assign Form</Modal.Title>
            <p className="afm-header-subtitle">Send a published form to one or more employees to complete</p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="form-assign-modal-body">
        <div className="afm-section">
          <div className="afm-section-title"><i className="bi bi-diagram-3" aria-hidden="true" /> Where does this belong?</div>
          <Row className="g-2">
            <Col md={6}>
              <Form.Label>Project</Form.Label>
              <FormAssignmentProjectSelector
                value={form.project_id}
                onChange={(id) => { onChange("project_id", id); onChange("project_module_id", ""); }}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Project Module <span className="afm-optional">(optional)</span></Form.Label>
              <FormAssignmentModuleSelector
                projectId={form.project_id}
                value={form.project_module_id}
                onChange={(id) => onChange("project_module_id", id)}
              />
            </Col>
          </Row>
        </div>

        <div className="afm-section">
          <div className="afm-section-title"><i className="bi bi-file-earmark-text" aria-hidden="true" /> Which form?</div>
          <Row className="g-2">
            <Col md={12}>
              <FormAssignmentFormSelector
                projectId={form.project_id}
                projectModuleId={form.project_module_id}
                formTypeId={form.form_type_id}
                formId={form.form_id}
                onFormTypeChange={(id) => { onChange("form_type_id", id); onChange("form_id", ""); onChange("form_version_id", ""); }}
                onFormChange={onFormChange}
              />
            </Col>
          </Row>
        </div>

        <div className="afm-section">
          <div className="afm-section-title-row">
            <div className="afm-section-title"><i className="bi bi-people" aria-hidden="true" /> Who should complete it?</div>
            {form.user_ids.length > 0 && (
              <span className="afm-count-badge">{form.user_ids.length} selected</span>
            )}
          </div>
          <Row className="g-2">
            <Col md={12}>
              <Form.Label>Assign To</Form.Label>
              <FormAssignmentUserSelector value={form.user_ids} onChange={(ids) => onChange("user_ids", ids)} />
            </Col>
          </Row>
        </div>

        <div className="afm-section">
          <div className="afm-section-title"><i className="bi bi-calendar-event" aria-hidden="true" /> Schedule &amp; details</div>
          <Row className="g-2">
            <Col md={6}>
              <Form.Label>Due Date</Form.Label>
              <Form.Control type="datetime-local" value={form.due_at} onChange={(e) => onChange("due_at", e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Assignment Title</Form.Label>
              <Form.Control
                value={form.assignment_title}
                placeholder={selectedFormRow ? `${selectedFormRow.name} — assignment` : ""}
                onChange={(e) => onChange("assignment_title", e.target.value)}
              />
            </Col>
            <Col md={12}>
              <Form.Label>Instructions <span className="afm-optional">(optional)</span></Form.Label>
              <Form.Control as="textarea" rows={2} value={form.instructions} onChange={(e) => onChange("instructions", e.target.value)} />
            </Col>
          </Row>
        </div>

        <div className="afm-section">
          <div className="afm-section-title"><i className="bi bi-sliders" aria-hidden="true" /> Submission rules</div>
          <div className="afm-toggle-row">
            <label className={`afm-toggle${form.is_mandatory ? " afm-toggle-checked" : ""}`}>
              <Form.Check type="checkbox" checked={form.is_mandatory} onChange={(e) => onChange("is_mandatory", e.target.checked)} />
              <span className="afm-toggle-icon"><i className="bi bi-exclamation-circle" aria-hidden="true" /></span>
              <span className="afm-toggle-label">Mandatory</span>
            </label>
            <label className={`afm-toggle${form.allow_multiple_submissions ? " afm-toggle-checked" : ""}`}>
              <Form.Check type="checkbox" checked={form.allow_multiple_submissions} onChange={(e) => onChange("allow_multiple_submissions", e.target.checked)} />
              <span className="afm-toggle-icon"><i className="bi bi-arrow-repeat" aria-hidden="true" /></span>
              <span className="afm-toggle-label">Allow multiple submissions</span>
            </label>
            <label className={`afm-toggle${form.allow_late_submission ? " afm-toggle-checked" : ""}`}>
              <Form.Check type="checkbox" checked={form.allow_late_submission} onChange={(e) => onChange("allow_late_submission", e.target.checked)} />
              <span className="afm-toggle-icon"><i className="bi bi-clock-history" aria-hidden="true" /></span>
              <span className="afm-toggle-label">Allow late submission</span>
            </label>
          </div>
          {form.allow_multiple_submissions && (
            <Row className="g-2 afm-max-submissions-row">
              <Col md={4}>
                <Form.Label className="small mb-0">Max submissions</Form.Label>
                <Form.Control type="number" min="1" value={form.maximum_submissions} onChange={(e) => onChange("maximum_submissions", e.target.value)} />
              </Col>
            </Row>
          )}
        </div>

        <Accordion className="afm-advanced">
          <Accordion.Item eventKey="0">
            <Accordion.Header><i className="bi bi-gear me-2" aria-hidden="true" />Advanced <span className="afm-optional ms-1">(optional)</span></Accordion.Header>
            <Accordion.Body>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Check type="checkbox" label="Allow save draft" checked={form.allow_save_draft} onChange={(e) => onChange("allow_save_draft", e.target.checked)} />
                </Col>
                <Col md={4}>
                  <Form.Label className="small mb-0">Reviewer (for future use)</Form.Label>
                  <Form.Select value={form.reviewer_id} onChange={(e) => onChange("reviewer_id", e.target.value)}>
                    <option value="">None</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="small mb-0">Approver (for future use)</Form.Label>
                  <Form.Select value={form.approver_id} onChange={(e) => onChange("approver_id", e.target.value)}>
                    <option value="">None</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </Form.Select>
                </Col>
                <Col md={12} className="mt-2">
                  <Form.Label className="small mb-0">
                    Lock evaluated employee <span className="afm-optional">(optional)</span>
                  </Form.Label>
                  <div className="form-text small mb-1">
                    If you already know who this form is ABOUT (e.g. the presenter of a Knowledge Sharing
                    session), locking it here prefills that field read-only for every respondent — they can
                    never accidentally select a different employee.
                  </div>
                  <EmployeePicker
                    endpoint="/form-assignments/subject-lock-options/search"
                    value={form.locked_subject_user_id || null}
                    onChange={(userId) => onChange("locked_subject_user_id", userId != null ? String(userId) : "")}
                    placeholder="Search employee by name, email, designation..."
                  />
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Modal.Body>
      <Modal.Footer className="unit-modal-footer">
        <Button className="unit-btn unit-btn-light" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          className="unit-btn unit-btn-primary"
          onClick={onSave}
          disabled={saving || !form.project_id || !form.form_version_id || form.user_ids.length === 0 || !form.assignment_title.trim()}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Assigning...
            </>
          ) : (
            <>
              <i className="bi bi-send-check me-2" aria-hidden="true" />
              Assign Form
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
